/**
 * Client for the online lobby service (worker/index.ts): lists games on this network, reserves a
 * four-letter code for a hosted game and keeps its listing fresh. Connection setup then runs over
 * WsSignalling to /api/rooms/<code>; gameplay itself goes directly between players.
 */
import { currentPass } from '../gate';

export interface LobbySummary {
  code: string;
  name: string;
  players: number;
  maxPlayers: number;
  map: string;
}

export interface ListingInfo {
  name: string;
  players: number;
  maxPlayers: number;
  map: string;
}

const HEARTBEAT_MS = 10_000;

/** Every request to the lobby service carries the pass from the start-up gate. */
const authed = (init: RequestInit = {}): RequestInit => ({ ...init, headers: { ...(init.headers as Record<string, string> | undefined), 'x-fc-pass': currentPass() ?? '' } });

/** The lobby service lives on the same origin that serves the game. */
export function roomUrl(code: string): string {
  const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${location.host}/api/rooms/${code}`;
}

export async function listLobbies(signal?: AbortSignal): Promise<LobbySummary[]> {
  const r = await fetch('/api/lobbies', authed({ signal, cache: 'no-store' }));
  if (!r.ok) throw new Error(`Lobby service unavailable (${r.status}).`);
  const body = (await r.json()) as { lobbies?: LobbySummary[] };
  return body.lobbies ?? [];
}

/** A hosted game's listing: reserved on creation, refreshed while open, removed on close. */
export class HostedListing {
  private timer: ReturnType<typeof setInterval> | null = null;
  private info: ListingInfo | null = null;
  private closed = false;

  private constructor(readonly code: string, private token: string) {}

  static async create(): Promise<HostedListing> {
    const r = await fetch('/api/lobbies', authed({ method: 'POST' }));
    const body = (await r.json().catch(() => ({}))) as { code?: string; token?: string; error?: string };
    if (!r.ok || !body.code || !body.token) throw new Error(body.error ?? `Lobby service unavailable (${r.status}).`);
    return new HostedListing(body.code, body.token);
  }

  /** Publish now and keep publishing until closed. Call again whenever the lobby changes. */
  update(info: ListingInfo): void {
    if (this.closed) return;
    this.info = info;
    void this.send();
    this.timer ??= setInterval(() => void this.send(), HEARTBEAT_MS);
  }

  private async send(): Promise<void> {
    if (this.closed || !this.info) return;
    try {
      await fetch(`/api/lobbies/${this.code}`, authed({ method: 'PUT', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ token: this.token, ...this.info }) }));
    } catch {
      /* transient: the next heartbeat retries */
    }
  }

  close(): void {
    if (this.closed) return;
    this.closed = true;
    if (this.timer) clearInterval(this.timer);
    void fetch(`/api/lobbies/${this.code}?token=${encodeURIComponent(this.token)}`, authed({ method: 'DELETE', keepalive: true })).catch(() => undefined);
  }
}
